import type { OfferRewardDto } from "../api/dto/offers.dto.js";
import type { KeyHuntBalancesDto, KeyHuntDto, KeyHuntPayloadDto } from "../api/dto/key_hunt.dto.js";
import {
  chooseKeyHuntPrize,
  claimKeyHuntPrize,
  claimKeyHuntTask,
  debugCompleteKeyHunt,
  debugExpireKeyHunt,
  debugResetKeyHunt,
  getKeyHuntConditional,
} from "../api/requests/key_hunt.requests.js";
import { EventPayloadListeners, eventPayloadKey, type EventPayloadListener } from "../api/event_payload_key.js";
import { infiniteHeartsUntilMs, serverUntilMs } from "../api/player_sync.js";
import { CumulativeBoosterService } from "../cumulative_boosters/cumulative_booster_service.js";
import { NetworkManager } from "../network_manager.js";
import { applyLocalEventUnlock } from "../offline/local_event_unlock.js";
import type { LocalMetricEvent } from "../offline/local_metrics.js";
import { OfflineEconomy } from "../offline/offline_economy.js";
import { OfflineJournal } from "../offline/offline_journal.js";
import { shouldGoLocal } from "../offline/offline_policy.js";
import { PersistentCache } from "../offline/persistent_cache.js";
import { ServerClock } from "../offline/server_clock.js";
import { StatisticsStorage } from "../storage/statistics_storage.js";
import { isNetworkishError } from "../utils/network_errors.js";
import { KeyHuntAnalytics } from "./key_hunt_analytics.js";
import { KeyHuntSignals } from "./key_hunt_signals.js";
import { applyKeyHuntMetrics, keyHuntTitleKey, refreshKeyHuntFlags, toOfferReward } from "./key_hunt_rules.js";


export const KEY_HUNT_KEYS_NEEDED = 3;

const PERSIST_KEY = "key_hunt_v1";
const CACHE_TTL_MS = 30_000;
const EXPIRED_RETRY_MS = 15_000;
const STALE_REFETCH_DELAY_MS = 1_500;

export interface KeyHuntTask
{
  id: string;
  metric: string;
  param: string | null;
  /** Localization key of the title, `{0}` is the target. */
  title_key: string;
  target: number;
  progress: number;
  claimed: boolean;
  /** Granted next to the key. */
  reward: OfferRewardDto;
}

export interface KeyHuntState
{
  period_start: string;
  period_end_ms: number;
  keys_needed: number;
  tasks: KeyHuntTask[];
  prize_options: OfferRewardDto[];
  /** Index into prize_options, null until the player picks one. */
  chosen_prize: number | null;
  prize_claimed: boolean;
}

export type KeyHuntTaskStatus = "progress" | "completed" | "claimed";

interface PersistedKeyHunt
{
  payload: KeyHuntPayloadDto;
  etag?: string | null;
}

function emptyState(): KeyHuntState
{
  return { period_start: "", period_end_ms: 0, keys_needed: KEY_HUNT_KEYS_NEEDED, tasks: [], prize_options: [], chosen_prize: null, prize_claimed: false };
}

class KeyHuntServiceImpl
{
  private payload: KeyHuntPayloadDto | null = null;
  private etag: string | null = null;
  private fetched_at = 0;
  private pending_fetch: Promise<KeyHuntPayloadDto | null> | null = null;
  private stale_timer: ReturnType<typeof setTimeout> | null = null;
  private rev = 0;
  private readonly listeners = new EventPayloadListeners<KeyHuntPayloadDto>();

  constructor()
  {
    const stored = PersistentCache.read<PersistedKeyHunt>( PERSIST_KEY );
    if ( stored?.payload && typeof stored.payload === "object" )
    {
      this.payload = stored.payload;
      this.etag = typeof stored.etag === "string" ? stored.etag : null;
    }
    KeyHuntSignals.subscribe( () => this.markStale() );
  }

  public nowMs(): number
  {
    return ServerClock.nowMs();
  }

  public getPayload(): KeyHuntPayloadDto | null
  {
    this.unlockLocally();
    return this.payload;
  }

  public onChange( listener: EventPayloadListener<KeyHuntPayloadDto> ): () => void
  {
    return this.listeners.add( listener );
  }

  public fetchedAgoMs(): number
  {
    return this.fetched_at > 0 ? Date.now() - this.fetched_at : Number.POSITIVE_INFINITY;
  }

  public hasEvent(): boolean
  {
    const hunt = this.payload?.key_hunt;
    return !!hunt && hunt.tasks.length > 0 && !this.isPeriodOver() && ( this.payload!.enabled || !!this.payload!.locked );
  }

  public async fetch( force = false ): Promise<KeyHuntPayloadDto | null>
  {
    this.unlockLocally();
    const max_age = this.isPeriodOver() ? EXPIRED_RETRY_MS : CACHE_TTL_MS;
    if ( !force && this.payload && Date.now() - this.fetched_at < max_age )
      return this.payload;
    if ( !NetworkManager.isOnline() )
      return this.payload;
    if ( this.pending_fetch ) return this.pending_fetch;

    this.pending_fetch = this.fetchInternal();
    try
    {
      return await this.pending_fetch;
    }
    finally
    {
      this.pending_fetch = null;
    }
  }

  private async fetchInternal(): Promise<KeyHuntPayloadDto | null>
  {
    try
    {
      const res = await getKeyHuntConditional( this.etag );
      this.fetched_at = Date.now();
      if ( res.status === 304 ) return this.payload;
      this.adoptPayload( res.data, res.etag );
      return this.payload;
    }
    catch ( e )
    {
      console.warn( "Key Hunt fetch failed:", e );
      return this.payload;
    }
  }

  private adoptPayload( payload: KeyHuntPayloadDto, etag: string | null ): void
  {
    const changed = !this.payload || eventPayloadKey( this.payload ) !== eventPayloadKey( payload );
    this.payload = payload;
    this.etag = etag;
    this.persist();
    if ( payload.enabled ) KeyHuntAnalytics.observe( payload.key_hunt );
    if ( payload.auto_claimed?.length )
    {
      if ( payload.balances ) this.applyBalances( payload.balances );
      KeyHuntAnalytics.autoClaimed( payload.auto_claimed );
    }
    if ( changed ) this.notify();
  }

  public markStale(): void
  {
    this.fetched_at = 0;
    if ( this.stale_timer !== null ) clearTimeout( this.stale_timer );
    this.stale_timer = setTimeout( () =>
    {
      this.stale_timer = null;
      void this.fetch( true );
    }, STALE_REFETCH_DELAY_MS );
  }

  public revision(): number
  {
    return this.rev;
  }

  public applyLocalProgress( events: LocalMetricEvent[] ): void
  {
    const hunt = this.activeHunt();
    if ( !hunt || !applyKeyHuntMetrics( hunt, events ) ) return;
    this.persist();
    this.notify();
  }

  public get(): KeyHuntState
  {
    const hunt = this.activeHunt();
    if ( !hunt ) return emptyState();
    return {
      period_start: hunt.period_start,
      period_end_ms: Date.parse( hunt.period_end ),
      keys_needed: hunt.keys_needed,
      tasks: hunt.tasks.map( task => ( {
        id: task.id,
        metric: task.metric,
        param: task.param,
        title_key: keyHuntTitleKey( task ),
        target: task.target,
        progress: task.claimed ? task.target : task.progress,
        claimed: task.claimed,
        reward: toOfferReward( task.reward ),
      } ) ),
      prize_options: hunt.prize_options.map( toOfferReward ),
      chosen_prize: hunt.chosen_prize,
      prize_claimed: hunt.prize_claimed,
    };
  }

  public isActive(): boolean
  {
    return this.activeHunt() !== null;
  }

  public timeLeftMs(): number
  {
    const end = this.payload?.key_hunt ? Date.parse( this.payload.key_hunt.period_end ) : 0;
    return Number.isFinite( end ) ? Math.max( 0, end - this.nowMs() ) : 0;
  }

  public taskStatus( task: Pick<KeyHuntTask, "claimed" | "progress" | "target"> ): KeyHuntTaskStatus
  {
    if ( task.claimed ) return "claimed";
    return task.progress >= task.target ? "completed" : "progress";
  }

  public keysNeeded(): number
  {
    return this.activeHunt()?.keys_needed ?? KEY_HUNT_KEYS_NEEDED;
  }

  public keys(): number
  {
    const hunt = this.activeHunt();
    return hunt ? Math.min( hunt.keys_needed, hunt.keys ) : 0;
  }

  public chosenPrize(): OfferRewardDto | null
  {
    const hunt = this.activeHunt();
    const prize = hunt && hunt.chosen_prize !== null ? hunt.prize_options[hunt.chosen_prize] : null;
    return prize ? toOfferReward( prize ) : null;
  }

  /** The safe is ready to open: all keys collected, prize not taken yet. */
  public isSafeReady(): boolean
  {
    const hunt = this.activeHunt();
    return !!hunt && hunt.keys >= hunt.keys_needed && !hunt.prize_claimed;
  }

  /** For the lobby badge: tasks to claim plus a ready safe. */
  public claimableCount(): number
  {
    const hunt = this.activeHunt();
    if ( !hunt || hunt.prize_claimed ) return 0;
    return hunt.tasks.filter( task => task.claimable ).length + ( this.isSafeReady() ? 1 : 0 );
  }

  /** Tasks in display order: ready to claim, in progress, claimed. */
  public orderedTasks(): KeyHuntTask[]
  {
    const rank: Record<KeyHuntTaskStatus, number> = { completed: 0, progress: 1, claimed: 2 };
    return this.get().tasks
      .map( ( task, index ) => ( { task, index } ) )
      .sort( ( a, b ) => rank[this.taskStatus( a.task )] - rank[this.taskStatus( b.task )] || a.index - b.index )
      .map( entry => entry.task );
  }

  public choosePrize( index: number ): boolean
  {
    const hunt = this.activeHunt();
    if ( !hunt || hunt.chosen_prize !== null || !hunt.prize_options[index] ) return false;
    hunt.chosen_prize = index;
    refreshKeyHuntFlags( hunt );
    this.persist();
    KeyHuntAnalytics.prizeChosen( hunt );

    const period_start = hunt.period_start;
    if ( shouldGoLocal() )
    {
      OfflineJournal.append( "key_hunt_prize_choose", { index, period_start } );
      return true;
    }
    void chooseKeyHuntPrize( index )
      .then( response => this.adoptHunt( response.key_hunt ) )
      .catch( e =>
      {
        if ( isNetworkishError( e ) ) OfflineJournal.append( "key_hunt_prize_choose", { index, period_start } );
        else this.recover( "choose prize", e );
      } );
    return true;
  }

  public claimTask( task_id: string ): boolean
  {
    const hunt = this.activeHunt();
    const task = hunt?.tasks.find( entry => entry.id === task_id );
    if ( !hunt || !task || task.claimed || task.progress < task.target || hunt.keys >= hunt.keys_needed ) return false;
    task.claimed = true;
    task.progress = task.target;
    refreshKeyHuntFlags( hunt );
    this.persist();
    KeyHuntAnalytics.taskClaimed( hunt, task );

    const period_start = hunt.period_start;
    const grantLocally = () =>
    {
      OfflineEconomy.grantRewards( [ task.reward ] );
      OfflineJournal.append( "key_hunt_task_claim", { task_id, period_start } );
    };
    if ( shouldGoLocal() )
    {
      grantLocally();
      return true;
    }
    void claimKeyHuntTask( task_id )
      .then( response =>
      {
        this.applyBalances( response.balances );
        this.adoptHunt( response.key_hunt );
      } )
      .catch( e =>
      {
        if ( isNetworkishError( e ) ) grantLocally();
        else this.recover( `claim task ${task_id}`, e );
      } );
    return true;
  }

  public claimPrize(): OfferRewardDto | null
  {
    const hunt = this.activeHunt();
    if ( !hunt || hunt.prize_claimed || hunt.keys < hunt.keys_needed || hunt.chosen_prize === null ) return null;
    const prize = hunt.prize_options[hunt.chosen_prize];
    if ( !prize ) return null;
    hunt.prize_claimed = true;
    refreshKeyHuntFlags( hunt );
    this.persist();
    KeyHuntAnalytics.prizeClaimed( hunt, [ prize ] );

    const period_start = hunt.period_start;
    const grantLocally = () =>
    {
      OfflineEconomy.grantRewards( [ prize ] );
      OfflineJournal.append( "key_hunt_prize_claim", { period_start } );
    };
    if ( shouldGoLocal() )
    {
      grantLocally();
      return toOfferReward( prize );
    }
    void claimKeyHuntPrize()
      .then( response =>
      {
        this.applyBalances( response.balances );
        this.adoptHunt( response.key_hunt );
      } )
      .catch( e =>
      {
        if ( isNetworkishError( e ) ) grantLocally();
        else this.recover( "claim prize", e );
      } );
    return toOfferReward( prize );
  }

  public async debugReset(): Promise<void>
  {
    const response = await debugResetKeyHunt();
    PersistentCache.remove( PERSIST_KEY );
    this.payload = null;
    this.adoptPayload( response, null );
  }

  public async debugComplete( task_id?: string ): Promise<void>
  {
    const response = await debugCompleteKeyHunt( task_id );
    this.adoptHunt( response.key_hunt );
  }

  public async debugExpire(): Promise<void>
  {
    const response = await debugExpireKeyHunt();
    this.adoptPayload( response, null );
  }

  private activeHunt(): KeyHuntDto | null
  {
    this.unlockLocally();
    const hunt = this.payload?.enabled ? this.payload.key_hunt : null;
    if ( !hunt || hunt.tasks.length === 0 || this.isPeriodOver() ) return null;
    return hunt;
  }

  private adoptHunt( hunt: KeyHuntDto ): void
  {
    if ( !this.payload ) return;
    if ( this.payload.key_hunt && this.payload.key_hunt.period_start !== hunt.period_start ) return;
    this.payload.key_hunt = hunt;
    this.persist();
    this.notify();
  }

  private notify(): void
  {
    this.rev++;
    this.listeners.notify( this.payload );
  }

  private recover( action: string, error: unknown ): void
  {
    console.warn( `[KeyHunt] ${action} failed:`, error );
    void this.fetch( true ).then( () => this.notify() );
  }

  private unlockLocally(): void
  {
    if ( applyLocalEventUnlock( this.payload ) )
    {
      console.info( "[KeyHunt] unlocked locally by level" );
      this.persist();
    }
  }

  private applyBalances( balances: KeyHuntBalancesDto ): void
  {
    const stats = StatisticsStorage.load();
    StatisticsStorage.update( {
      coins: balances.coins,
      hearts: Math.max( stats.hearts, balances.hearts ),
      infinite_hearts_until: Math.max( stats.infinite_hearts_until, infiniteHeartsUntilMs( balances.infinite_hearts_until ) ),
      no_ads_until: Math.max( stats.no_ads_until, serverUntilMs( balances.no_ads_until ) ),
      fireworks: balances.boosters.fireworks,
      multiballs: balances.boosters.multi_balls,
      big_balls: balances.boosters.big_balls,
      infinite_balls: balances.boosters.infinite_balls,
    } );
    CumulativeBoosterService.applyInventory( balances.boosters );
  }

  private persist(): void
  {
    if ( !this.payload ) return;
    PersistentCache.write( PERSIST_KEY, { payload: this.payload, etag: this.etag } satisfies PersistedKeyHunt );
  }

  public isPeriodOver(): boolean
  {
    const end = this.payload?.key_hunt?.period_end;
    if ( !end ) return false;
    const end_ms = Date.parse( end );
    return Number.isFinite( end_ms ) && this.nowMs() >= end_ms;
  }
}

export const KeyHuntService = new KeyHuntServiceImpl();
